import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = <>
    <h3 className="text-2xl font-serif mb-6" data-id="element-43">Privacy Policy</h3>
    <div className="space-y-4 text-charcoal/80 leading-relaxed text-sm" data-id="element-44">
      <p data-id="element-45">
        <strong data-id="element-46">Last Updated:</strong> April 2026
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-47">
        1. Information We Collect
      </h4>
      <p data-id="element-48">
        We collect information you provide directly when purchasing our digital
        products, including your name, email address, and payment details.
        Payment processing is handled by secure third-party processors — we do
        not store your credit card information.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-49">
        2. How We Use Your Information
      </h4>
      <p data-id="element-50">
        Your information is used solely to deliver the purchased digital
        product, send order confirmations, and provide customer support. We may
        also send occasional product updates or related offers, from which you
        can unsubscribe at any time.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-51">
        3. Data Sharing
      </h4>
      <p data-id="element-52">
        We do not sell, rent, or share your personal information with third
        parties for marketing purposes. Data may be shared only with essential
        service providers (payment processors, email delivery) required to
        fulfill your order.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-53">4. Cookies</h4>
      <p data-id="element-54">
        Our website may use cookies to improve your browsing experience and
        analyze site traffic. You can control cookie preferences through your
        browser settings.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-55">
        5. Data Security
      </h4>
      <p data-id="element-56">
        We implement industry-standard security measures to protect your
        personal information. However, no method of electronic transmission is
        100% secure, and we cannot guarantee absolute security.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-57">
        6. Your Rights
      </h4>
      <p data-id="element-58">
        You have the right to access, correct, or delete your personal data at
        any time. To exercise these rights, please reach out through the
        channels provided with your purchase.
      </p>
    </div>
  </>;
const termsContent = <>
    <h3 className="text-2xl font-serif mb-6" data-id="element-59">Terms of Service</h3>
    <div className="space-y-4 text-charcoal/80 leading-relaxed text-sm" data-id="element-60">
      <p data-id="element-61">
        <strong data-id="element-62">Last Updated:</strong> April 2026
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-63">
        1. Digital Product
      </h4>
      <p data-id="element-64">
        The 7-Day Face Sculpt is a digital educational product delivered
        instantly upon purchase. By completing your purchase, you agree to these
        terms.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-65">
        2. Educational Disclaimer
      </h4>
      <p data-id="element-66">
        The content provided is for educational and informational purposes only.
        It is not intended as medical advice, diagnosis, or treatment. Always
        consult with a qualified healthcare professional before starting any new
        health, nutrition, or wellness protocol.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-67">
        3. Results Disclaimer
      </h4>
      <p data-id="element-68">
        Individual results may vary. Testimonials and examples used are not
        intended to guarantee that anyone will achieve the same or similar
        results. Your results depend on your individual effort, body
        composition, and adherence to the protocol.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-69">
        4. Intellectual Property
      </h4>
      <p data-id="element-70">
        All content within The 7-Day Face Sculpt — including text, images,
        graphics, and methods — is protected by copyright. You may not
        reproduce, distribute, or share any part of this product without written
        permission.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-71">
        5. Refund Policy
      </h4>
      <p data-id="element-72">
        Due to the digital nature of this product, all sales are final. If you
        experience any technical issues accessing your purchase, we will work to
        resolve them promptly.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-73">
        6. Limitation of Liability
      </h4>
      <p data-id="element-74">
        The 7-Day Face Sculpt and its creators shall not be held liable for any
        damages arising from the use or inability to use the product. Use of
        this product is at your own risk.
      </p>

      <h4 className="font-semibold text-base text-charcoal mt-6" data-id="element-75">
        7. Changes to Terms
      </h4>
      <p data-id="element-76">
        We reserve the right to update these terms at any time. Continued use of
        the product after changes constitutes acceptance of the revised terms.
      </p>
    </div>
  </>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-white/60 py-8 text-center text-sm" data-id="element-77">
        <div className="max-w-6xl mx-auto px-6" data-id="element-78">
          <p data-id="element-79">
            © {new Date().getFullYear()} The 7-Day Face Sculpt. All rights
            reserved.
          </p>
          <div className="flex items-center justify-center gap-6 mt-4" data-id="element-80">
            <button onClick={() => setActiveModal('privacy')} className="text-white/50 hover:text-white transition-colors duration-200 underline underline-offset-2" data-id="element-81">
              Privacy Policy
            </button>
            <span className="text-white/20" data-id="element-82">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-white/50 hover:text-white transition-colors duration-200 underline underline-offset-2" data-id="element-83">
              Terms of Service
            </button>
          </div>
          <p className="mt-4 text-white/40 text-xs max-w-2xl mx-auto" data-id="element-84">
            Disclaimer: The information provided is for educational purposes
            only and is not intended as medical advice. Results may vary. Always
            consult with a healthcare professional before starting any new
            health or wellness protocol.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-85">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-86">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-87">
              <div className="flex items-center justify-between p-6 border-b border-sage/10" data-id="element-88">
                <span className="text-xs font-semibold text-sage uppercase tracking-widest" data-id="element-89">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </span>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-charcoal/5 transition-colors" data-id="element-90">
                  <XIcon className="w-5 h-5 text-charcoal/60" data-id="element-91" />
                </button>
              </div>
              <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(80vh-72px)]" data-id="element-92">
                {activeModal === 'privacy' ? privacyContent : termsContent}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}